import React, { useState, useEffect, useMemo } from 'react';
import { FiBriefcase, FiArchive, FiUsers } from 'react-icons/fi';
import api from '../api/config';
import './css/HomeAdmin.css';

const HomeAdmin = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardStats = async () => {
            try {
                const response = await api.get('/api/dashboard/stats');
                setStats(response.data);
            } catch (err) {
                setError('Veriler yüklenirken bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardStats();
    }, []);

    // En yüksek başvuru sayısını performans için memoize ediyoruz
    const maxApplicationCount = useMemo(() => {
        if (!stats?.applicationsByJob || stats.applicationsByJob.length === 0) return 1;
        return Math.max(...stats.applicationsByJob.map(j => j.basvuru_sayisi || 0));
    }, [stats]);

    if (loading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
            </div>
        );
    }
    
    if (error) return <div className="error-message">{error}</div>;
    if (!stats) return null;

    const jobTypeLabels = {
        merkez: 'Merkez',
        yurt_personel: 'Yurt Personel',
        yurt_yonetici: 'Yurt Yönetici',
        neva_yemek: 'Neva Yemek'
    };

    return (
        <div className="dashboard">
            <h1 className="dashboard-title">Yönetim Paneli</h1>

            <div className="stats-container">
                {/* Aktif İlanlar */}
                <div className="stats-card">
                    <div className="stats-icon text-blue">
                        <FiBriefcase />
                    </div>
                    <div className="stats-info">
                        <h3>Aktif İlanlar</h3>
                        <span className="main-number">{stats.activeJobs?.total || 0}</span>
                        <div className="sub-numbers">
                            <span>Merkez: <strong>{stats.activeJobs?.merkez || 0}</strong></span>
                            <span>Yurt Personel: <strong>{stats.activeJobs?.yurt_personel || 0}</strong></span>
                            <span>Yurt Yönetici: <strong>{stats.activeJobs?.yurt_yonetici || 0}</strong></span>
                            <span>Neva Yemek: <strong>{stats.activeJobs?.neva_yemek || 0}</strong></span>
                        </div>
                    </div>
                </div>

                {/* Arşivlenen İlanlar */}
                <div className="stats-card">
                    <div className="stats-icon text-orange">
                        <FiArchive />
                    </div>
                    <div className="stats-info">
                        <h3>Arşivlenen İlanlar</h3>
                        <span className="main-number">
                            {(stats.archivedJobs?.merkez || 0) + 
                             (stats.archivedJobs?.yurt_personel || 0) + 
                             (stats.archivedJobs?.yurt_yonetici || 0) + 
                             (stats.archivedJobs?.neva_yemek || 0)}
                        </span>
                        <div className="sub-numbers">
                            <span>Merkez: <strong>{stats.archivedJobs?.merkez || 0}</strong></span>
                            <span>Yurt Personel: <strong>{stats.archivedJobs?.yurt_personel || 0}</strong></span>
                            <span>Yurt Yönetici: <strong>{stats.archivedJobs?.yurt_yonetici || 0}</strong></span>
                            <span>Neva Yemek: <strong>{stats.archivedJobs?.neva_yemek || 0}</strong></span>
                        </div>
                    </div>
                </div>

                {/* Toplam Başvuru */}
                <div className="stats-card">
                    <div className="stats-icon text-green">
                        <FiUsers />
                    </div>
                    <div className="stats-info">
                        <h3>Toplam Başvuru</h3>
                        <span className="main-number">{stats.totalApplicants || 0}</span>
                        <span className="sub-text">Sistemdeki genel başvuru sayısı</span>
                    </div>
                </div>
            </div>

            <div className="dashboard-grid">
                {/* Son Başvurular */}
                <div className="data-card">
                    <h2>Son Başvurular</h2>
                    <div className="table-wrapper">
                        {stats.recentApplications?.length > 0 ? (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Başvuran</th>
                                        <th>E-posta</th>
                                        <th>İlan</th>
                                        <th>Tarih</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recentApplications.map((app, index) => (
                                        <tr key={index}>
                                            <td className="font-medium">{app.ad} {app.soyad}</td>
                                            <td>{app.email}</td>
                                            <td><span className="table-job-title">{app.ilan_basligi}</span></td>
                                            <td className="text-muted">{new Date(app.basvuru_tarihi).toLocaleDateString('tr-TR')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-state">Henüz başvuru bulunmuyor.</div>
                        )}
                    </div>
                </div>

                {/* İlan Bazlı Başvurular */}
                <div className="data-card">
                    <h2>İlan Bazlı Başvurular</h2>
                    <div className="job-stats">
                        {stats.applicationsByJob?.length > 0 ? (
                            stats.applicationsByJob.map((job, index) => {
                                const percentage = ((job.basvuru_sayisi || 0) / maxApplicationCount) * 100;
                                return (
                                    <div key={index} className="job-stat-item">
                                        <div className="job-header">
                                            <span className="job-title">{job.ilan_basligi}</span>
                                            <span className="job-type">
                                                {jobTypeLabels[job.type] || 'Diğer'}
                                            </span>
                                        </div>
                                        <div className="progress-container">
                                            <div className="progress-bar">
                                                <div className="progress" style={{ width: `${percentage}%` }}></div>
                                            </div>
                                            <span className="count">{job.basvuru_sayisi}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="empty-state">İlanlara ait başvuru verisi bulunamadı.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomeAdmin;